import type React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FormTextareaProps {
  name: string;
  label?: string;
  placeholder?: string;
  rules?: any;
  className?: string;
  disabled?: boolean;
  defaultValue?: string;
  rows?: number;
  resize?: boolean;
  onValueChange?: (value: string) => void;
}

export const FormTextarea = ({
  name,
  label,
  placeholder,
  rules,
  className,
  disabled,
  defaultValue = '',
  rows = 4,
  resize = false,
  onValueChange,
}: FormTextareaProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({
        field: { onChange, value, ...field },
        fieldState: { error },
      }) => {
        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          const newValue = e.target.value;
          onChange(newValue);
          onValueChange?.(newValue);
        };

        return (
          <div className="space-y-2">
            {label && (
              <label
                htmlFor={name}
                className="text-sm font-medium text-foreground"
              >
                {label}
              </label>
            )}

            <div
              className={cn(
                'flex w-full rounded-md border border-input px-3 py-2 text-sm ring-offset-background text-foreground focus-within:ring-2 focus-within:ring-secondary focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                error && 'border-red-500 ring-1 ring-red-200',
                className,
              )}
            >
              <textarea
                id={name}
                {...field}
                value={value ?? ''}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                rows={rows}
                className={cn(
                  'w-full resize-none  outline-none',
                  resize && 'resize-y',
                )}
              />
            </div>

            {error && (
              <p className="flex items-center gap-1.5 px-1 text-xs font-medium text-red-500">
                <AlertCircle className="h-3.5 w-3.5" />
                {error.message || 'This field is required'}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};
